import * as types from "../actions/actionTypes";
import { TagNames, SettingNames } from "../../lib/constants";

const initialState = {
  updateTimestamp: null,
  systemSettings: {
    fetched: false,
    loading: false,
    success: true,
    data: {
      applicationSettings: {
        individualApplicationReasons: [],
        organizationApplicationReasons: []
      },
      campaignSettings: {
        categories: [],
        campaignIndivCategories: [],
        fromEmailAddress: [],
        locationGroups: [],
        years: [],
        months: []
      },
      fundSettings: {
        banks: [],
        defaultIsraelFund: null,
        defaultUSFund: null,
        depositSources: [],
        withdrawalTypes: []
      },
      otherSettings: {
        applicationStatuses: [],
        campaignCategories: [],
        campaignListStatuses: [],
        streetNames: [],
        cities: [],
        currencyTypes: [],
        departments: [],
        nationalities: [],
        orgEmploymentTypes: [],
        permissionTypes: [],
        previousEmploymentTypes: [],
        recipientStatuses: [],
        recipientTypes: [],
        roles: [],
        unitTypes: []
      },
      paymentSettings: {
        returnAddress: {},
        signatures: [],
        currencyConversionRate: {}
      },
      printSettings: {
        cardTypes: [],
        cloudPrintIsAuthorized: false,
        cloudPrintUserName: ""
      },
      recipientSettings: {
        individualTags: [],
        organizationTags: [],
        usPersonTitles: [],
        israelPersonTitles: [],
        usInstitutionTitles: [],
        israelInstitutionTitles: []
      }
    },
    reloading: {
      allSettings: {
        loading: false,
        success: true
      },
      paymentSettings: {
        loading: false,
        success: true
      },
      recipientSettings: {
        loading: false,
        success: true
      },
      applicationSettings: {
        loading: false,
        success: true
      },
      campaignSettings: {
        loading: false,
        success: true
      },
      fundSettings: {
        loading: false,
        success: true
      },
      printSettings: {
        loading: false,
        success: true
      },
      otherSettings: {
        loading: false,
        success: true
      }
    }
  },
  printers: {
    fetched: false,
    loading: false,
    success: true,
    message: "",
    data: []
  }
};

function getSystemSettingsStateAfterAddition(
  state,
  settingCategoryName,
  settingName,
  updatedSetting
) {
  return {
    ...state,
    systemSettings: {
      ...state.systemSettings,
      data: {
        ...state.systemSettings.data,
        [settingCategoryName]: {
          ...state.systemSettings.data[settingCategoryName],
          [settingName]: [
            ...state.systemSettings.data[settingCategoryName][settingName],
            updatedSetting
          ]
        }
      }
    }
  };
}

function getSystemSettingsStateAfterUpdate(
  state,
  settingCategoryName,
  settingName,
  updatedSetting
) {
  const updatedSettings = [
    ...state.systemSettings.data[settingCategoryName][settingName]
  ];
  const updatedSettingIndex = updatedSettings
    .map(settingTag => settingTag.key)
    .indexOf(updatedSetting.key);
  if (updatedSettingIndex >= 0) {
    updatedSettings[updatedSettingIndex] = updatedSetting;
  }
  return {
    ...state,
    systemSettings: {
      ...state.systemSettings,
      data: {
        ...state.systemSettings.data,
        [settingCategoryName]: {
          ...state.systemSettings.data[settingCategoryName],
          [settingName]: updatedSettings
        }
      }
    }
  };
}

export default (state = initialState, action) => {
  const updatedState = { ...state, updateTimestamp: Date.now() };
  switch (action.type) {
    case types.START_GET_SYSTEM_SETTINGS:
      return Object.assign({}, state, {
        systemSettings: { ...state.systemSettings, loading: true }
      });
    case types.GET_SYSTEM_SETTINGS:
      return Object.assign({}, updatedState, {
        systemSettings: {
          ...state.systemSettings,
          ...action.response,
          data: { ...state.systemSettings.data, ...action.response.data },
          fetched: true
        }
      });

    case types.START_GET_PARTIAL_SYSTEM_SETTINGS:
      return {
        ...state,
        systemSettings: {
          ...state.systemSettings,
          reloading: {
            ...state.systemSettings.reloading,
            [action.setting]: {
              ...state.systemSettings.reloading[action.setting],
              loading: true
            }
          }
        }
      };
    case types.GET_PARTIAL_SYSTEM_SETTINGS:
      return {
        ...updatedState,
        systemSettings: {
          ...state.systemSettings,
          reloading: {
            ...state.systemSettings.reloading,
            [action.setting]: {
              loading: false,
              success: action.response.success
            }
          },
          data: action.response.success
            ? {
                ...state.systemSettings.data,
                [action.setting]: action.response.data[action.setting]
              }
            : { ...state.systemSettings.data }
        }
      };

    case types.START_GET_PRINTERS:
      return Object.assign({}, state, {
        printers: { ...initialState.printers, loading: true }
      });
    case types.GET_PRINTERS:
      return Object.assign({}, state, {
        printers: { ...state.printers, ...action.response, fetched: true }
      });

    case types.ADD_TAG:
      if (!action.response.success) return state;
      switch (action.tagName) {
        case TagNames.IndividualTags:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                recipientSettings: {
                  ...state.systemSettings.data.recipientSettings,
                  individualTags: [
                    ...state.systemSettings.data.recipientSettings.individualTags,
                    action.response.data
                  ]
                }
              }
            }
          };
        case TagNames.OrganizationTags:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                recipientSettings: {
                  ...state.systemSettings.data.recipientSettings,
                  organizationTags: [
                    ...state.systemSettings.data.recipientSettings.organizationTags,
                    action.response.data
                  ]
                }
              }
            }
          };
        case TagNames.IndividualApplicationReasons:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                applicationSettings: {
                  ...state.systemSettings.data.applicationSettings,
                  individualApplicationReasons: [
                    ...state.systemSettings.data.applicationSettings.individualApplicationReasons,
                    action.response.data
                  ]
                }
              }
            }
          };
        case TagNames.OrganizationApplicationReasons:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                applicationSettings: {
                  ...state.systemSettings.data.applicationSettings,
                  organizationApplicationReasons: [
                    ...state.systemSettings.data.applicationSettings
                      .organizationApplicationReasons,
                    action.response.data
                  ]
                }
              }
            }
          };
        case TagNames.USPersonTitles:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                recipientSettings: {
                  ...state.systemSettings.data.recipientSettings,
                  usPersonTitles: [
                    ...state.systemSettings.data.recipientSettings
                      .usPersonTitles,
                    action.response.data
                  ]
                }
              }
            }
          };
        case TagNames.IsraelPersonTitles:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                recipientSettings: {
                  ...state.systemSettings.data.recipientSettings,
                  israelPersonTitles: [
                    ...state.systemSettings.data.recipientSettings
                      .israelPersonTitles,
                    action.response.data
                  ]
                }
              }
            }
          };
        case TagNames.USInstitutionTitles:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                recipientSettings: {
                  ...state.systemSettings.data.recipientSettings,
                  usInstitutionTitles: [
                    ...state.systemSettings.data.recipientSettings
                      .usInstitutionTitles,
                    action.response.data
                  ]
                }
              }
            }
          };
        case TagNames.IsraelInstitutionTitles:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                recipientSettings: {
                  ...state.systemSettings.data.recipientSettings,
                  israelInstitutionTitles: [
                    ...state.systemSettings.data.recipientSettings
                      .israelInstitutionTitles,
                    action.response.data
                  ]
                }
              }
            }
          };
        case TagNames.Banks:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                fundSettings: {
                  ...state.systemSettings.data.fundSettings,
                  banks: [
                    ...state.systemSettings.data.fundSettings.banks,
                    action.response.data
                  ]
                }
              }
            }
          };
        case TagNames.CardTypes:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                printSettings: {
                  ...state.systemSettings.data.printSettings,
                  cardTypes: [
                    ...state.systemSettings.data.printSettings.cardTypes,
                    action.response.data
                  ]
                }
              }
            }
          };
        case TagNames.CampaignCategories:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                campaignSettings: {
                  ...state.systemSettings.data.campaignSettings,
                  categories: [
                    ...state.systemSettings.data.campaignSettings.categories,
                    action.response.data
                  ]
                }
              }
            }
          };
        case TagNames.CampaignIndivCategories:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                campaignSettings: {
                  ...state.systemSettings.data.campaignSettings,
                  campaignIndivCategories: [
                    ...state.systemSettings.data.campaignSettings.campaignIndivCategories,
                    action.response.data
                  ]
                }
              }
            }
          };

        case TagNames.LocationGroups:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                campaignSettings: {
                  ...state.systemSettings.data.campaignSettings,
                  locationGroups: [
                    ...state.systemSettings.data.campaignSettings
                      .locationGroups,
                    action.response.data
                  ]
                }
              }
            }
          };
        case TagNames.WithdrawalTypes:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                fundSettings: {
                  ...state.systemSettings.data.fundSettings,
                  withdrawalTypes: [
                    ...state.systemSettings.data.fundSettings.withdrawalTypes,
                    action.response.data
                  ]
                }
              }
            }
          };
        case TagNames.DepositSources:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                fundSettings: {
                  ...state.systemSettings.data.fundSettings,
                  depositSources: [
                    ...state.systemSettings.data.fundSettings.depositSources,
                    action.response.data
                  ]
                }
              }
            }
          };
        default:
          return state;
      }
    case types.UPDATE_TAG:
      if (!action.response.success) return state;
      switch (action.tagName) {
        case TagNames.WithdrawalTypes:
          return getSystemSettingsStateAfterUpdate(
            updatedState,
            "fundSettings",
            "withdrawalTypes",
            action.response.data
          );
        case TagNames.DepositSources:
          return getSystemSettingsStateAfterUpdate(
            updatedState,
            "fundSettings",
            "depositSources",
            action.response.data
          );
        case TagNames.USPersonTitles:
          return getSystemSettingsStateAfterUpdate(
            updatedState,
            "recipientSettings",
            "usPersonTitles",
            action.response.data
          );
        case TagNames.IsraelPersonTitles:
          return getSystemSettingsStateAfterUpdate(
            updatedState,
            "recipientSettings",
            "israelPersonTitles",
            action.response.data
          );
        case TagNames.USInstitutionTitles:
          return getSystemSettingsStateAfterUpdate(
            updatedState,
            "recipientSettings",
            "usInstitutionTitles",
            action.response.data
          );
        case TagNames.IsraelInstitutionTitles:
          return getSystemSettingsStateAfterUpdate(
            updatedState,
            "recipientSettings",
            "israelInstitutionTitles",
            action.response.data
          );
        default:
          return state;
      }
    case types.DELETE_TAG:
      if (!action.response.success) return state;
      let index,
        tags = [];
      switch (action.tagName) {
        case TagNames.IndividualTags:
          tags = [
            ...state.systemSettings.data.recipientSettings.individualTags
          ];
          index = tags.map(tag => tag.key).indexOf(action.tagId);
          if (index > -1) tags.splice(index, 1);
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                recipientSettings: {
                  ...state.systemSettings.data.recipientSettings,
                  individualTags: tags
                }
              }
            }
          };
        case TagNames.OrganizationTags:
          tags = [
            ...state.systemSettings.data.recipientSettings.organizationTags
          ];
          index = tags.map(tag => tag.key).indexOf(action.tagId);
          if (index > -1) tags.splice(index, 1);
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                recipientSettings: {
                  ...state.systemSettings.data.recipientSettings,
                  organizationTags: tags
                }
              }
            }
          };
        case TagNames.IndividualApplicationReasons:
          tags = [
            ...state.systemSettings.data.applicationSettings
              .individualApplicationReasons
          ];
          index = tags.map(tag => tag.key).indexOf(action.tagId);
          if (index > -1) tags.splice(index, 1);
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                applicationSettings: {
                  ...state.systemSettings.data.applicationSettings,
                  individualApplicationReasons: tags
                }
              }
            }
          };
        case TagNames.OrganizationApplicationReasons:
          tags = [
            ...state.systemSettings.data.applicationSettings
              .organizationApplicationReasons
          ];
          index = tags.map(tag => tag.key).indexOf(action.tagId);
          if (index > -1) tags.splice(index, 1);
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                applicationSettings: {
                  ...state.systemSettings.data.applicationSettings,
                  organizationApplicationReasons: tags
                }
              }
            }
          };
        case TagNames.USPersonTitles:
          tags = [
            ...state.systemSettings.data.recipientSettings.usPersonTitles
          ];
          index = tags.map(tag => tag.key).indexOf(action.tagId);
          if (index > -1) tags.splice(index, 1);
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                recipientSettings: {
                  ...state.systemSettings.data.recipientSettings,
                  usPersonTitles: tags
                }
              }
            }
          };
        case TagNames.IsraelPersonTitles:
          tags = [
            ...state.systemSettings.data.recipientSettings.israelPersonTitles
          ];
          index = tags.map(tag => tag.key).indexOf(action.tagId);
          if (index > -1) tags.splice(index, 1);
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                recipientSettings: {
                  ...state.systemSettings.data.recipientSettings,
                  israelPersonTitles: tags
                }
              }
            }
          };
        case TagNames.USInstitutionTitles:
          tags = [
            ...state.systemSettings.data.recipientSettings.usInstitutionTitles
          ];
          index = tags.map(tag => tag.key).indexOf(action.tagId);
          if (index > -1) tags.splice(index, 1);
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                recipientSettings: {
                  ...state.systemSettings.data.recipientSettings,
                  usInstitutionTitles: tags
                }
              }
            }
          };
        case TagNames.IsraelInstitutionTitles:
          tags = [
            ...state.systemSettings.data.recipientSettings
              .israelInstitutionTitles
          ];
          index = tags.map(tag => tag.key).indexOf(action.tagId);
          if (index > -1) tags.splice(index, 1);
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                recipientSettings: {
                  ...state.systemSettings.data.recipientSettings,
                  israelInstitutionTitles: tags
                }
              }
            }
          };
        case TagNames.Banks:
          tags = [...state.systemSettings.data.fundSettings.banks];
          index = tags.map(tag => tag.key).indexOf(action.tagId);
          if (index > -1) tags.splice(index, 1);
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                fundSettings: {
                  ...state.systemSettings.data.fundSettings,
                  banks: tags
                }
              }
            }
          };
        case TagNames.CardTypes:
          tags = [...state.systemSettings.data.printSettings.cardTypes];
          index = tags.map(tag => tag.key).indexOf(action.tagId);
          if (index > -1) tags.splice(index, 1);
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                printSettings: {
                  ...state.systemSettings.data.printSettings,
                  cardTypes: tags
                }
              }
            }
          };
        case TagNames.CampaignCategories:
          tags = [...state.systemSettings.data.campaignSettings.categories];
          index = tags.map(cat => cat.campaignCategoryId).indexOf(action.tagId);
          if (index > -1) tags.splice(index, 1);
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                campaignSettings: {
                  ...state.systemSettings.data.campaignSettings,
                  categories: tags
                }
              }
            }
          };
        case TagNames.CampaignIndivCategories:
          tags = [
            ...state.systemSettings.data.campaignSettings.campaignIndivCategories
          ];
          console.log(tags);
          index = tags.map(tag => tag.key).indexOf(action.tagId);
          if (index > -1) tags.splice(index, 1);
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                campaignSettings: {
                  ...state.systemSettings.data.campaignSettings,
                  campaignIndivCategories: tags
                }
              }
            }
          };
        case TagNames.LocationGroups:
          tags = [...state.systemSettings.data.campaignSettings.locationGroups];
          index = tags.map(loc => loc.groupId).indexOf(action.tagId);
          if (index > -1) tags.splice(index, 1);
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                campaignSettings: {
                  ...state.systemSettings.data.campaignSettings,
                  locationGroups: tags
                }
              }
            }
          };
        default:
          return state;
      }
    case types.UPDATE_SETTING:
      if (!action.response.success) return state;
      switch (action.settingName) {
        case SettingNames.CurrencyConversionRate:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                paymentSettings: {
                  ...state.systemSettings.data.paymentSettings,
                  currencyConversionRate: {
                    ...state.systemSettings.data.paymentSettings
                      .currencyConversionRate,
                    value: action.value
                  }
                }
              }
            }
          };
          case SettingNames.FromEmailAddress:
            return {
              ...updatedState,
              systemSettings: {
                ...state.systemSettings,
                data: {
                  ...state.systemSettings.data,
                  campaignSettings: {
                    ...state.systemSettings.data.campaignSettings,
                    fromEmailAddress: {
                      ...state.systemSettings.data.campaignSettings
                        .fromEmailAddress,
                      value: action.value
                    }
                  }
                }
              }
            };
        case SettingNames.ReturnAddress:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                paymentSettings: {
                  ...state.systemSettings.data.paymentSettings,
                  returnAddress: action.value
                }
              }
            }
          };
        case SettingNames.DefaultUSFund:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                fundSettings: {
                  ...state.systemSettings.data.fundSettings,
                  defaultUSFund: action.value
                }
              }
            }
          };
        case SettingNames.DefaultILFund:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                fundSettings: {
                  ...state.systemSettings.data.fundSettings,
                  defaultIsraelFund: action.value
                }
              }
            }
          };
        case SettingNames.Signatures:
          return updatedState;
        case TagNames.LocationGroups:
          return updatedState;
        case SettingNames.AuthorizeCloudPrint:
          return {
            ...updatedState,
            systemSettings: {
              ...state.systemSettings,
              data: {
                ...state.systemSettings.data,
                printSettings: {
                  ...state.systemSettings.data.printSettings,
                  cloudPrintIsAuthorized: true,
                  cloudPrintUserName: action.response.data
                }
              }
            }
          };

        default:
          return state;
      }
    default:
      return state;
  }
};



// WEBPACK FOOTER //
// ./src/redux/reducers/SettingsReducer.js